/**
 * SubmissionActions Component
 * Provides action buttons for reviewing submissions
 *
 * Workflow:
 * - Start Review: Creates staging_library entry (WIP), links submission, sets to in_review
 * - Request Info: Asks contributor for more information
 * - Reject: Rejects the submission with reason
 *
 * Once in_review, the game is managed via staging_library.
 * Status syncs back automatically when staging transitions.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography
} from '@mui/material';
import {
  RateReview as ReviewIcon,
  Close as RejectIcon,
  HelpOutline as InfoIcon
} from '@mui/icons-material';
import {
  useAcceptSubmission,
  useRejectSubmission,
  useRequestSubmissionInfo
} from '../../hooks/useSubmissions';
import { SUBMISSION_STATUS } from '../../utils/constants';

export const SubmissionActions = ({ submission, onActionComplete }) => {
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [startReviewDialogOpen, setStartReviewDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [gameCodeError, setGameCodeError] = useState('');

  // Form state
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [infoRequest, setInfoRequest] = useState('');
  const [gameCode, setGameCode] = useState('');

  // Mutations
  const startReviewMutation = useAcceptSubmission(); // Renamed: creates staging as WIP
  const rejectMutation = useRejectSubmission();
  const requestInfoMutation = useRequestSubmissionInfo();

  if (!submission) return null;

  const isLoading = startReviewMutation.isLoading ||
    rejectMutation.isLoading ||
    requestInfoMutation.isLoading;

  // Determine available actions based on status
  // Start Review: available when not yet in staging (pending, needs_info, response_received)
  const canStartReview = [
    SUBMISSION_STATUS.PENDING,
    SUBMISSION_STATUS.NEEDS_INFO,
    SUBMISSION_STATUS.RESPONSE_RECEIVED
  ].includes(submission.status) && !submission.stagedGameId;

  // Request Info: available before review starts
  const canRequestInfo = [
    SUBMISSION_STATUS.PENDING,
    SUBMISSION_STATUS.RESPONSE_RECEIVED
  ].includes(submission.status);

  // Reject: available before review starts or after response
  const canReject = [
    SUBMISSION_STATUS.PENDING,
    SUBMISSION_STATUS.NEEDS_INFO,
    SUBMISSION_STATUS.RESPONSE_RECEIVED
  ].includes(submission.status);

  // Show info message when in_review (managed via staging)
  const isInReview = submission.status === SUBMISSION_STATUS.IN_REVIEW;
  const isInBeta = submission.status === SUBMISSION_STATUS.IN_BETA;
  const isAccepted = submission.status === SUBMISSION_STATUS.ACCEPTED;

  // Open the start review dialog with suggested gameCode
  const openStartReviewDialog = () => {
    // Use suggested gameCode from submission, or generate from gameName
    const suggestedCode = submission.gameCode ||
      (submission.gameName || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 50);
    setGameCode(suggestedCode);
    setGameCodeError('');
    setStartReviewDialogOpen(true);
  };

  // Validate gameCode format
  const validateGameCode = (code) => {
    if (!code || code.length < 2) {
      return 'Game code must be at least 2 characters';
    }
    if (code.length > 100) {
      return 'Game code must be 100 characters or less';
    }
    if (!/^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*(\.[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*)?$/.test(code)) {
      return 'Game code must contain only letters, numbers, hyphens, and periods (for expansions)';
    }
    if (code.startsWith('-') || code.endsWith('-') || code.startsWith('.') || code.endsWith('.')) {
      return 'Game code cannot start or end with hyphen or period';
    }
    return null;
  };

  const handleStartReview = async () => {
    setError('');
    setGameCodeError('');

    // Validate gameCode
    const validationError = validateGameCode(gameCode);
    if (validationError) {
      setGameCodeError(validationError);
      return;
    }

    try {
      await startReviewMutation.mutateAsync({
        id: submission.id,
        data: {
          admin_notes: adminNotes,
          gameCode: gameCode
        }
      });
      setStartReviewDialogOpen(false);
      setAdminNotes('');
      setGameCode('');
      onActionComplete?.();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to start review';
      // Check if it's a gameCode conflict error
      if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
        setGameCodeError('This game code is already in use. Please choose a different one.');
      } else {
        setError(errorMsg);
      }
    }
  };

  const handleReject = async () => {
    setError('');
    if (!rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    try {
      await rejectMutation.mutateAsync({
        id: submission.id,
        data: { rejection_reason: rejectReason, admin_notes: adminNotes }
      });
      setRejectDialogOpen(false);
      setRejectReason('');
      setAdminNotes('');
      onActionComplete?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject submission');
    }
  };

  const handleRequestInfo = async () => {
    setError('');
    if (!infoRequest.trim()) {
      setError('Please specify what information you need');
      return;
    }
    try {
      await requestInfoMutation.mutateAsync({
        id: submission.id,
        data: { followup_message: infoRequest }
      });
      setInfoDialogOpen(false);
      setInfoRequest('');
      onActionComplete?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to request information');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Status messages for submissions being managed via staging */}
      {isInReview && submission.stagedGameId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This submission is now in staging review.{' '}
          <a href={`/games/${submission.stagedGameId}`} style={{ color: 'inherit' }}>
            View staged game
          </a>
        </Alert>
      )}

      {isInBeta && (
        <Alert severity="success" sx={{ mb: 2 }}>
          This game is currently in beta testing. Status will update when promoted to production.
        </Alert>
      )}

      {isAccepted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          This submission has been accepted and is now in production.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {canStartReview && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReviewIcon />}
            onClick={openStartReviewDialog}
            disabled={isLoading}
          >
            Start Review
          </Button>
        )}

        {canRequestInfo && (
          <Button
            variant="outlined"
            color="info"
            startIcon={<InfoIcon />}
            onClick={() => setInfoDialogOpen(true)}
            disabled={isLoading}
          >
            Request Info
          </Button>
        )}

        {canReject && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<RejectIcon />}
            onClick={() => setRejectDialogOpen(true)}
            disabled={isLoading}
          >
            Reject
          </Button>
        )}
      </Box>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Submission</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            sx={{ mt: 1, mb: 2 }}
            placeholder="Explain why this submission is being rejected..."
          />

          <TextField
            label="Additional Notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={isLoading}>
            Reject Submission
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Additional Information</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The submitter will be notified and asked to provide additional information.
          </Typography>

          <TextField
            label="What information do you need?"
            value={infoRequest}
            onChange={(e) => setInfoRequest(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            sx={{ mt: 1 }}
            placeholder="Please provide specific questions or clarifications needed..."
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRequestInfo} variant="contained" color="info" disabled={isLoading}>
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Review Dialog */}
      <Dialog open={startReviewDialogOpen} onClose={() => setStartReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Review - Confirm Game Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Confirm or modify the game code before creating the staging entry.
            This code will be used as the unique identifier for the game.
          </Typography>

          <TextField
            label="Game Code"
            value={gameCode}
            onChange={(e) => {
              setGameCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              setGameCodeError('');
            }}
            fullWidth
            required
            error={!!gameCodeError}
            helperText={gameCodeError || 'Lowercase letters, numbers, hyphens, and colons (e.g., basegame:expansion)'}
            sx={{ mt: 1, mb: 2 }}
            placeholder="e.g., catan-base-game"
          />

          <TextField
            label="Admin Notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartReviewDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStartReview} variant="contained" color="primary" disabled={isLoading}>
            Start Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
